import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
  ThumbUp as ApprovedIcon,
  ThumbDown as RejectedIcon,
  TrendingUp as HighConfidenceIcon
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export default function ClassificationStats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/accounting/classification/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setStats(response.data.data);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching classification stats:', err);
      setError(err.response?.data?.error?.message || 'Failed to load statistics');
      setLoading(false);
    }
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value || 0);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  if (!stats) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        No statistics available
      </Alert>
    );
  }

  const statCards = [
    {
      title: 'Total Unbooked',
      value: formatNumber(stats.total_unmatched),
      icon: <AssignmentIcon sx={{ fontSize: 32, color: '#2196f3', opacity: 0.7 }} />,
      color: '#2196f3',
      subtitle: 'Incl. pre-2025 history'
    },
    {
      title: 'AI Classified',
      value: formatNumber(stats.classified),
      icon: <CheckCircleIcon sx={{ fontSize: 32, color: '#9c27b0', opacity: 0.7 }} />,
      color: '#9c27b0',
      subtitle: 'AI suggestions created'
    },
    {
      title: 'Pending Review',
      value: formatNumber(stats.pending_review),
      icon: <PendingIcon sx={{ fontSize: 32, color: '#ff9800', opacity: 0.7 }} />,
      color: '#ff9800',
      subtitle: 'Awaiting approval'
    },
    {
      title: 'Approved',
      value: formatNumber(stats.approved),
      icon: <ApprovedIcon sx={{ fontSize: 32, color: '#4caf50', opacity: 0.7 }} />,
      color: '#4caf50',
      subtitle: 'Human verified'
    },
    {
      title: 'Rejected',
      value: formatNumber(stats.rejected),
      icon: <RejectedIcon sx={{ fontSize: 32, color: '#f44336', opacity: 0.7 }} />,
      color: '#f44336',
      subtitle: 'Suggestions rejected'
    },
    {
      title: 'High Confidence',
      value: formatNumber(stats.high_confidence),
      icon: <HighConfidenceIcon sx={{ fontSize: 32, color: '#00bcd4', opacity: 0.7 }} />,
      color: '#00bcd4',
      subtitle: '≥80% confidence'
    }
  ];

  return (
    <Box mb={4}>
      <Typography variant="h6" fontWeight="600" gutterBottom mb={2}>
        Classification Pipeline Overview
      </Typography>

      <Grid container spacing={2}>
        {statCards.map((card, idx) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={idx}>
            <Paper
              sx={{
                p: 2.5,
                borderLeft: `4px solid ${card.color}`,
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 3
                }
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {card.title}
                  </Typography>
                  <Typography variant="h5" fontWeight="700">
                    {card.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {card.subtitle}
                  </Typography>
                </Box>
                {card.icon}
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Additional Insights */}
      {stats.pending_review > 0 && (
        <Box mt={3}>
          <Paper sx={{ p: 2, backgroundColor: 'rgba(255, 152, 0, 0.1)', borderLeft: '4px solid #ff9800' }}>
            <Typography variant="subtitle2" fontWeight="600" color="warning.main">
              Action Required
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {formatNumber(stats.pending_review)} classifications are awaiting your review. Review and approve high-confidence suggestions to speed up reconciliation.
            </Typography>
          </Paper>
        </Box>
      )}

      {stats.high_confidence > 0 && stats.high_confidence === stats.pending_review && (
        <Box mt={2}>
          <Paper sx={{ p: 2, backgroundColor: 'rgba(76, 175, 80, 0.1)', borderLeft: '4px solid #4caf50' }}>
            <Typography variant="subtitle2" fontWeight="600" color="success.main">
              High Quality Suggestions
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              All pending classifications have high confidence (≥80%). These can likely be bulk-approved.
            </Typography>
          </Paper>
        </Box>
      )}
    </Box>
  );
}
